import React, {ReactElement, useCallback, useMemo} from 'react';
import {Button, Typography} from '@customink/pigment-react';
import AlertTriangle from '../../assets/icons/alertwarningtriangle.svg';
import {useAppDispatch, useAppSelector, useStoreSlug} from '../../hooks';
import {
  selectPendingDetailsCount,
  removePendingDetails
} from '../../slices/storesSlice';
import {useUpdateStoreMutation} from '../../api/storesApi';
import AccordionMenu from '../AccordionMenu';
import AccordionMenuItem from '../AccordionMenu/AccordionMenuItem';
import LogoForm from './LogoForm';
import HeroImageForm from './HeroImageForm';
import PromoMessageForm from '../PromoMessageForm';
import './index.scss';
import {StoreUpdateParamsType} from '../../models/storeDetails';

const SaveAndApplyButtons: React.FC = (): ReactElement => {
  const dispatch = useAppDispatch();
  const pendingChangesCount = useAppSelector(selectPendingDetailsCount);
  const pendingChanges = useAppSelector(
    state => state.stores.pendingDetails
  ) as StoreUpdateParamsType;
  const urlSlug = useStoreSlug();
  const [triggerUseUpdateStoreMutation] = useUpdateStoreMutation();

  const onSave = useCallback(() => {
    triggerUseUpdateStoreMutation({details: pendingChanges, slug: urlSlug});
    dispatch(removePendingDetails());
  }, [urlSlug, pendingChanges, triggerUseUpdateStoreMutation]);

  const onClear = useCallback(() => {
    dispatch(removePendingDetails());
  }, [dispatch]);

  const unsavedChangesMessage = useMemo(() => {
    if (pendingChangesCount > 1) {
      return (
        <span className="bolder">{pendingChangesCount} unsaved changes.</span>
      );
    }
    return (
      <span className="bolder">{pendingChangesCount} unsaved change.</span>
    );
  }, [pendingChangesCount]);

  return (
    pendingChangesCount > 0 && (
      <div className="saveAndApply">
        <div className="changeMessage">
          <img src={AlertTriangle} alt="Warning" />{' '}
          <Typography variant="captionLarge">
            You have {unsavedChangesMessage}
          </Typography>
        </div>
        <div className="saveAndApplyButtonRow">
          <Button
            className="discardButton button"
            onClick={onClear}
            variant="link">
            <span>Discard</span>
          </Button>
          <Button onClick={onSave} variant="secondary" className="button">
            Save Changes
          </Button>
        </div>
      </div>
    )
  );
};

type CustomizationPanelProps = {
  storeSlug: string;
};

const CustomizationPanel: React.FC<CustomizationPanelProps> = ({
  storeSlug
}: CustomizationPanelProps): ReactElement => {
  return (
    <div className="customizationPanel">
      <AccordionMenu title="Store Appearance">
        <AccordionMenuItem title="Logo">
          <LogoForm />
        </AccordionMenuItem>
        <AccordionMenuItem title="Banner">
          <HeroImageForm />
        </AccordionMenuItem>
        <AccordionMenuItem title="Announcement">
          <PromoMessageForm storeSlug={storeSlug} />
        </AccordionMenuItem>
      </AccordionMenu>
      <SaveAndApplyButtons />
    </div>
  );
};

export default CustomizationPanel;
